import * as firebase from "firebase/app";
import "firebase/firestore";
import "firebase/functions";
import "firebase/storage";
import { GeoFirestore } from "geofirestore";
import constants from "../constants";
import Model from './model';
import Parking from './parking.model';

export default class BaseUser extends Model {
  uid;
  email;
  displayName;
  raw;
  rawProfile;
  role;
  profile;
  _selfRef;

  constructor(firebaseUser, profileDoc) {
    super();
    const { email, displayName, uid } = firebaseUser;
    this.uid = uid;
    this.email = email;
    this.displayName = displayName ? displayName : profileDoc.displayName;
    this.raw = firebaseUser;
    if (profileDoc) {
      this.rawProfile = profileDoc;
      this.profile = this.rawProfile;
      this.role = this.profile.role;
      if (!this.email) {
        this.email = this.profile.email;
      }
    }
  }

  static async findUserByRut(rut) {
    const lookup = firebase.functions().httpsCallable("lookupUserByRut");
    let authUser;
    try {
      authUser = await lookup({ rut });
    } catch (e) {
      throw new Error("The user you are looking up does not exist.");
    }
    return new BaseUser(authUser.data, {}).toSubClass();
  }

  static async findUser(email) {
    const lookup = firebase.functions().httpsCallable("lookupUserByEmail");
    const params = { email };
    const db = firebase.firestore();
    try {
      const authUser = await lookup(params);
      const profile = await db.collection(constants.collections.profiles).doc(authUser.data.uid).get();
      if (profile.exists) {
        const profileData = profile.data()
        if (profileData.d) {
          return new BaseUser(authUser.data, profileData.d).toSubClass();
        } else {
          return new BaseUser(authUser.data, profileData).toSubClass();
        }
      }
    } catch (e) {
      console.log('findUserError:', e);
    }
    return undefined;
  }

  toSubClass() {
    if (this.role in roleToClass) {
      return new roleToClass[this.role](this);
    }
    return this;
  }
}

class Admin extends BaseUser {
  constructor(user) {
    super(user.raw, user.rawProfile);
    const db = firebase.firestore();
    this._selfRef = db.collection(constants.collections.profiles).doc(this.uid);
  }
}

class Community extends BaseUser {

  _geoDB;
  _db;

  constructor(user) {
    super(user.raw, user.rawProfile);
    this._db = firebase.firestore();
    this._geoDB = new GeoFirestore(this._db);
    this._selfRef = this._db.collection(constants.collections.profiles).doc(this.uid);
    this._selfGeoRef = this._geoDB.collection(constants.collections.profiles).doc(this.uid);
  }

  async getPictureDownloadURL() {
    if (this.picture) {
      const storageRef = firebase.storage().ref();
      const pictureRef = await storageRef.child(this.picture);
      return await pictureRef.getDownloadURL();
    }
    return undefined;
  }

  async getParkingsSnapshots() {
    const snapshots = await this._selfRef.collection(constants.collections.parkings).get();
    return snapshots.docs;
  }

  async getAvailableParkingsSnapshots(type = 'private') {
    let snapshots;
    try {
      snapshots = await this._selfRef.collection(constants.collections.parkings)
          .where('available', '==', true)
          .where('type', '==', type)
          .get();
    } catch (e) {
      console.log(`getAvailableParkingsSnapshots(${type}) Error:`, e);
    }
    return snapshots.docs;
  }

  async getAvailableVisitorsParkingsSnapshots() {
    const snapshots = await this._selfRef.collection(constants.collections.parkings).where('createdBy', '==', this._selfRef).where('available', '==', true).get();
    return snapshots.docs;
  }

  async pickOneAvailableVisitorsParking() {
    const availables = await this.getAvailableParkingsSnapshots('community');
    const queryDocumentSnapshot =
      availables[Math.floor(Math.random() * availables.length)];
    try {
      await queryDocumentSnapshot.ref.update({ available: false });
    } catch (e) {
      console.log('Error updating parking availability:', e);
      throw e;
    }
    const hydration = await Parking.buildHydration(queryDocumentSnapshot);
    const parking = new Parking(hydration);
    parking.available = false;
    return parking;
  }

  async pickOneAvailableParking() {
    const availables = await this.getAvailableParkingsSnapshots();
    const queryDocumentSnapshot =
      availables[Math.floor(Math.random() * availables.length)];
    await queryDocumentSnapshot.ref.update({ available: false });
    const hydration = await Parking.buildHydration(queryDocumentSnapshot);
    const parking = new Parking(hydration);
    parking.available = false;
    return parking;
  }

  async getLogs() {
    let snap;
    try {
      snap = await this._db.collection(constants.collections.logs)
          .where('community', '==', this.selfRef)
          .orderBy('timestamp', 'desc')
          .get();
    } catch (e) {
      console.log('getLogs Error', e);
      throw e;
    }
    return await Promise.all(snap.docs.map(async doc => {
      if (doc.exists) {
        let log, reservationSnap, reservedBySnap, userSnap;
        try {
          log = (await doc.ref.get()).data();
        } catch (e) {
          console.log('getLogs Error getting log document', e);
          throw e;
        }
        try {
          reservationSnap  = await log.reservation.get();
        } catch (e) {
          console.log('getLogs Error getting log reservation snapshot', e);
          throw e;
        }

        const reservation = reservationSnap.data();
        try {
          reservedBySnap = await reservation.reservedBy.get();
        } catch (e) {
          console.log('getLogs Error getting log reservation.reservedBy snapshot', e);
          throw e;
        }
        const reservedBy = reservedBySnap.data();
        try {
          userSnap = await reservation.user.get();
        } catch (e) {
          console.log('getLogs Error getting log reservation.user snapshot', e);
          throw e;
        }
        const user = userSnap.data();
        log.user = user;
        log.reservedBy = reservedBy;
        const type = `${reservation.type[0].toUpperCase()}${reservation.type.slice(1)}`;
        log.type = type;
        return log;
      } else {
        return undefined;
      }
    }));
  }
}

class Worker extends BaseUser {
  constructor(user) {
    super(user.raw, user.rawProfile);
    const db = firebase.firestore();
    this._selfRef = db.collection(constants.collections.profiles).doc(this.uid);
  }
}

class Partner extends BaseUser {
  constructor(user) {
    super(user.raw, user.rawProfile);
    const db = firebase.firestore();
    this._selfRef = db.collection(constants.collections.profiles).doc(this.uid);
  }

  async getParkings() {
    const querySnap = await this.rawProfile.createdBy
      .collection(constants.collections.parkings)
      .where('createdBy', '==', this._selfRef).get();
    return await Promise.all(querySnap.docs.map(async (doc) => {
      const hydration = await Parking.buildHydration(doc);
      return new Parking(hydration);
    }));
  }

  async getAvailableParkings() {
    const db = firebase.firestore();
    const querySnap = await db.doc(`${constants.collections.profiles}/${this.rawProfile.createdBy.id}`)
      .collection(constants.collections.parkings)
      .where('createdBy', '==', this._selfRef)
      .where('type', '==', 'private')
      .where('available', '==', true).get();
    return await Promise.all(querySnap.docs.map(async (doc) => {
      const hydration = await Parking.buildHydration(doc);
      return new Parking(hydration);
    }));
  }

  async getFirstAvailableParking() {
    const parkings = await this.getAvailableParkings();
    return parkings[0];
  }

  async getCommunity() {
    const communitySnap = await this.rawProfile.createdBy.get();
    if (communitySnap.exists) {
      const profile = communitySnap.data().d;
      const user = await BaseUser.findUser(profile.email);
      return user;
    }
    return undefined;
  }
}

class User extends BaseUser {
  constructor(user) {
    super(user.raw, user.rawProfile);
    const db = firebase.firestore();
    this._selfRef = db.collection(constants.collections.profiles).doc(this.uid);
  }

  getVehicle() {
    return {
      car: this.profile.car,
      color: this.profile.color,
      plate: this.profile.plate
    };
  }
}

const roleToClass = {
  admin: Admin,
  community: Community,
  partner: Partner,
  worker: Worker,
  user: User
};
